import { luscinia } from "../luscinia";
import { NewValueParams, SelectionChangedEvent } from "ag-grid-community";
import { RolesGrid } from "./Grids/RolesGrid";
import { PermissionObjectsGrid as PermissionObjectsGrid } from "./Grids/PermissionObjectsGrid";
import { IPermissionObjectRole, PanelData, PermissionObject, Role } from "./RolePanelTypes";
import { IPermissionGrid } from "./Grids/PermissionGrids/IPermissionGrid";
import { PermissionGridFactory } from "./Grids/PermissionGrids/PermissionGridFactory";
import { RecordPropertyRolePanel } from "./RecordPropertyRolePanel/RecordPropertyRolePanel";
import { PermissionObjectIds } from "./Enums/PermissionObjectIds";
import { GridViewRolePanel } from "./GridViewRolePanel/GridViewRolePanel";

import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";

export class RolePanel {
    panelData: PanelData;

    private readonly roleControlPanelEl: HTMLElement;
    private readonly saveChangesButton: HTMLElement;

    private readonly rolesGrid: RolesGrid;
    private readonly permissionObjectsGrid: PermissionObjectsGrid;

    private permissionGrid: IPermissionGrid<any>;
    private recordPropertyRolePanel: RecordPropertyRolePanel;
    private gridViewRolePanel: GridViewRolePanel;
    private readonly permissionPanel: HTMLElement;

    private readonly permissionGridFactory: PermissionGridFactory;

    constructor() {
        this.roleControlPanelEl = document.getElementById("RoleControlPanel");
        this.saveChangesButton = document.getElementById("SaveChangesButton");

        const rolesGridContainer = this.roleControlPanelEl.querySelector(".rolesGrid") as HTMLElement;
        this.rolesGrid = new RolesGrid(rolesGridContainer);

        const permissionObjectsGridContainer = this.roleControlPanelEl.querySelector(".permissionObjectGrid") as HTMLElement;
        this.permissionObjectsGrid = new PermissionObjectsGrid(permissionObjectsGridContainer);

        this.permissionPanel = this.roleControlPanelEl.querySelector(".permissionPanel") as HTMLElement;
        this.permissionGridFactory = new PermissionGridFactory();
    }

    async init() {
        await this.rolesGrid.init();
        await this.permissionObjectsGrid.init();

        const role = this.getSelectedRole();
        const permissionObject = this.getSelectedPermissionObject();

        this.addEventListeners();

        if (!role || !permissionObject) {
            return;
        }

        this.initializePermissionPanel(permissionObject, role);
    }
    private addEventListeners() {
        this.rolesGrid.api.addEventListener("selectionChanged", this.handleRoleSelectionChange.bind(this));
        this.permissionObjectsGrid.api.addEventListener("selectionChanged", this.handlePermissionObjectSelectionChange.bind(this));
        this.saveChangesButton.addEventListener("click", this.handleSaveChanges.bind(this));
    }
    getSelectedRole(): Role {
        return this.rolesGrid.api.getSelectedRows()[0];
    }
    private getSelectedPermissionObject(): PermissionObject {
        return this.permissionObjectsGrid.api.getSelectedRows()[0];
    }
    private async handleSaveChanges() {
        if (!this.panelData) return;
        const result = await fetch("/rolePanel/permissions", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify(this.panelData),
        });

        if (result.ok) {
            luscinia.showNotification("Success", "Changes saved successfully", "success");
        } else {
            var error = await result.text();
            luscinia.showNotification("Failed to save changes", error, "error");
        }

        this.reload();
    }
    private handleCellValueChanged(e: NewValueParams) {
        const rowData: IPermissionObjectRole = e.data;

        if (!this.panelData) {
            this.panelData = {
                MenuItemRoles: [],
                ReportRoles: [],
                RecordPropertyRoles: [],
                GridViewRoles: [],
                ExcelDropdownReportRoles: [],
                ExcelExportRoles: [],
                DashboardShortcutRoles: []
            };
        }

        const existingRowData = this.panelData[this.permissionGrid.id].find((item: IPermissionObjectRole) => item.Id === rowData.Id);
        if (existingRowData) {
            Object.assign(existingRowData, rowData);
        } else {
            this.panelData[this.permissionGrid.id].push(rowData);
        }
    }
    private reload() {
        const selectedPermissionObject = this.getSelectedPermissionObject();
        const selectedRole = this.getSelectedRole();

        if (selectedPermissionObject.Id === PermissionObjectIds.RecordRole) {
            this.permissionGrid.reload(`/${this.recordPropertyRolePanel.getSelectedRecordName()}/${selectedRole.Id}`, null);
        } else if (selectedPermissionObject.Id === PermissionObjectIds.GridViewRole) {
            this.permissionGrid.reload(`/${this.gridViewRolePanel.getSelectedRecordName()}/${selectedRole.Id}`, null);
        } else {
            this.permissionGrid.reload(`/${selectedRole.Id}`, null);
        }
    }
    private async initializePermissionPanel(permissionObject: PermissionObject, role: Role) {
        if (permissionObject.Id === PermissionObjectIds.RecordRole) {
            await this.initializeRecordRolePanel(role);
        } else if (permissionObject.Id === PermissionObjectIds.GridViewRole) {
            await this.initializeGridViewRolePanel(role);
        } else {
            await this.initializePermissionGrid(permissionObject, role);
        }
        this.permissionGrid.api.removeEventListener("cellValueChanged", this.handleCellValueChanged.bind(this));
        this.permissionGrid.api.addEventListener("cellValueChanged", this.handleCellValueChanged.bind(this));
    }
    private async initializeGridViewRolePanel(role: Role) {
        this.gridViewRolePanel = new GridViewRolePanel(this);
        await this.gridViewRolePanel.init(role.Id, this.panelData);
        this.permissionGrid = this.gridViewRolePanel.gridViewRolesGrid;
        this.permissionPanel.appendChild(this.gridViewRolePanel.gridViewRolePanelEl);
    }
    private async initializeRecordRolePanel(role: Role) {
        this.recordPropertyRolePanel = new RecordPropertyRolePanel(this);
        await this.recordPropertyRolePanel.init(role.Id, this.panelData);
        this.permissionGrid = this.recordPropertyRolePanel.recordPropertyRolesGrid;
        this.permissionPanel.appendChild(this.recordPropertyRolePanel.recordRolePanelEl);
    }
    private async initializePermissionGrid(permissionObject: PermissionObject, role: Role) {
        this.permissionGrid = this.permissionGridFactory.createOrGet(permissionObject.Id);
        await this.permissionGrid.initOrReload(`/${role.Id}`, this.panelData);
        this.permissionPanel.appendChild(this.permissionGrid.gridContainer);
    }
    private async handleRoleSelectionChange(e: SelectionChangedEvent<Role>) {
        const selectedRole = e.api.getSelectedRows()[0];
        const selectedPermissionObject = this.getSelectedPermissionObject();

        if (selectedPermissionObject.Id === PermissionObjectIds.RecordRole) {
            await this.permissionGrid.reload(`/${this.recordPropertyRolePanel.getSelectedRecordName()}/${selectedRole.Id}`, this.panelData);
        } else if (selectedPermissionObject.Id === PermissionObjectIds.GridViewRole) {
            await this.permissionGrid.reload(`/${this.gridViewRolePanel.getSelectedRecordName()}/${selectedRole.Id}`, this.panelData);
        } else {
            await this.permissionGrid.reload(`/${selectedRole.Id}`, this.panelData);
        }
    }
    private async handlePermissionObjectSelectionChange(e: SelectionChangedEvent<PermissionObject>) {
        const selectedPermissionObject = e.api.getSelectedRows()[0];
        const selectedRole = this.getSelectedRole();

        if (this.permissionGrid) {
            this.permissionGrid.destroy();
            this.permissionGrid = null;
        }

        if (this.recordPropertyRolePanel) {
            this.recordPropertyRolePanel.destroy();
            this.recordPropertyRolePanel = null;
        }

        if (this.gridViewRolePanel) {
            this.gridViewRolePanel.destroy();
            this.gridViewRolePanel = null;
        }

        this.permissionPanel.innerHTML = "";
        if (selectedPermissionObject.Id === PermissionObjectIds.RecordRole) {
            await this.initializeRecordRolePanel(selectedRole);
        } else if (selectedPermissionObject.Id === PermissionObjectIds.GridViewRole) {
            await this.initializeGridViewRolePanel(selectedRole);
        } else {
            await this.initializePermissionGrid(selectedPermissionObject, selectedRole);
        }
        this.permissionGrid.api.removeEventListener("cellValueChanged", this.handleCellValueChanged.bind(this));
        this.permissionGrid.api.addEventListener("cellValueChanged", this.handleCellValueChanged.bind(this));
    }
}
